import { getServerUri } from "./uriUtility";

export function formatNodeDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  const pad = (n) => (n < 10 ? "0" + n : "" + n);
  return `${pad(dt.getDate())}.${pad(dt.getMonth() + 1)}.${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

export function getCurrentNodeDate(separator = ".") {
  return formatNodeDate(new Date()).replace(/\./g, separator).replace(/(\d{2})\.(\d{2})\.(\d{4})/, (_, d, m, y) => {
    return `${d}${separator}${m}${separator}${y}`;
  });
}

// Match existing dialog date format: DD.MM.YYYY HH:mm
export function getCurrentHierarchyDate() {
  const newDate = new Date();
  const date = newDate.getDate();
  const month = newDate.getMonth() + 1;
  const year = newDate.getFullYear();
  const time = newDate.getHours();
  const minute = newDate.getMinutes() < 10 ? "0" + newDate.getMinutes() : newDate.getMinutes();
  return `${date < 10 ? `0${date}` : `${date}`}.${month < 10 ? `0${month}` : `${month}`}.${year} ${time}:${minute}`;
}

export function isVersionNode(node) {
  return node && node.data && node.data.fileType === "text";
}

export function buildFlatTreeFromVersions(versionRecords) {
  if (!Array.isArray(versionRecords)) return [];
  return versionRecords.map((v) => ({
    id: String(v._id),
    parent: 0,
    text: v.title,
    data: {
      fileType: "text",
      createdDate: formatNodeDate(v.dateCreated),
      updateDate: formatNodeDate(v.dateUpdated),
    },
  }));
}

export function mergeOrphanVersionsIntoTree(tree, versionRecords) {
  const next = Array.isArray(tree) ? [...tree] : [];
  const idsInTree = new Set(next.filter(isVersionNode).map((n) => String(n.id)));
  let changed = false;
  (versionRecords || []).forEach((v) => {
    const id = String(v._id);
    if (!idsInTree.has(id)) {
      next.push({
        id,
        parent: 0,
        text: v.title,
        data: {
          fileType: "text",
          createdDate: formatNodeDate(v.dateCreated),
          updateDate: formatNodeDate(v.dateUpdated),
        },
      });
      changed = true;
    }
  });
  return { tree: next, changed };
}

export function createFolderNode(title, parentId = 0) {
  const now = getCurrentHierarchyDate();
  return {
    id: Math.floor(Math.random() * 100000),
    parent: parentId,
    droppable: true,
    text: title,
    data: {
      createdDate: now,
      updateDate: now,
    },
  };
}

export function createVersionNode(version, parentId = 0) {
  return {
    id: String(version._id),
    parent: parentId,
    text: version.title,
    data: {
      fileType: "text",
      createdDate: formatNodeDate(version.dateCreated) || getCurrentHierarchyDate(),
      updateDate: formatNodeDate(version.dateUpdated) || getCurrentHierarchyDate(),
    },
  };
}

export function resolveFolderParentId(selectedFolderId) {
  if (selectedFolderId == null || selectedFolderId === 9999999) return 0;
  if (typeof selectedFolderId === "object" && selectedFolderId.id != null) return selectedFolderId.id;
  return 0;
}

export async function fetchHierarchy(baseID, companyID) {
  const response = await fetch(getServerUri("version/getHierarchy"), {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      accessToken: localStorage.getItem("session.accessToken"),
      baseID: String(baseID),
      companyID: String(companyID),
    }),
  });
  if (response.status !== 200) {
    return { tree: [], exists: false, ok: false };
  }
  const json = await response.json();
  const tree = Array.isArray(json.versionHierarchy) ? json.versionHierarchy : [];
  const hasContent = tree.length > 0;
  return { tree, exists: hasContent, ok: true };
}

export async function saveHierarchy(baseID, companyID, tree) {
  const response = await fetch(getServerUri("version/updateHierarchy"), {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      accessToken: localStorage.getItem("session.accessToken"),
      baseID: String(baseID),
      companyID: String(companyID),
      versionHierarchy: JSON.stringify({ tree }),
    }),
  });
  return response.status === 200;
}

export function bootstrapTreeFromVersions(versionRecords, folderNode) {
  const tree = buildFlatTreeFromVersions(versionRecords);
  if (folderNode) tree.push(folderNode);
  return tree;
}

export function addVersionToTree(tree, version, parentId = 0) {
  const next = Array.isArray(tree) ? [...tree] : [];
  next.push(createVersionNode(version, parentId));
  return next;
}

export function removeVersionFromTree(tree, versionId) {
  const id = String(versionId);
  return (Array.isArray(tree) ? tree : []).filter((n) => String(n.id) !== id);
}

export function renameVersionInTree(tree, versionId, newTitle) {
  const id = String(versionId);
  const now = getCurrentHierarchyDate();
  return (Array.isArray(tree) ? tree : []).map((n) => {
    if (String(n.id) === id && isVersionNode(n)) {
      return {
        ...n,
        text: newTitle,
        data: { ...n.data, updateDate: now },
      };
    }
    return n;
  });
}

export function collectVersionIdsInSubtree(tree, folderId) {
  const ids = [];
  const folderKey = folderId;
  function walk(parentId) {
    (tree || []).forEach((node) => {
      if (node.parent === parentId || String(node.parent) === String(parentId)) {
        if (isVersionNode(node)) {
          ids.push(String(node.id));
        } else {
          walk(node.id);
        }
      }
    });
  }
  walk(folderKey);
  return ids;
}

export function removeFolderFromTree(tree, folderId) {
  const toRemove = new Set([folderId]);
  let changed = true;
  while (changed) {
    changed = false;
    (tree || []).forEach((node) => {
      if (toRemove.has(node.parent) || toRemove.has(String(node.parent))) {
        if (!toRemove.has(node.id)) {
          toRemove.add(node.id);
          changed = true;
        }
      }
    });
  }
  return (tree || []).filter((n) => !toRemove.has(n.id));
}

export async function resolvePlayEngineTree(baseID, companyID, versionRecords) {
  const { tree, exists, ok } = await fetchHierarchy(baseID, companyID);
  if (!ok) {
    return {
      tree: buildFlatTreeFromVersions(versionRecords),
      hierarchyEnabled: false,
      persisted: false,
    };
  }
  if (!exists) {
    return {
      tree: buildFlatTreeFromVersions(versionRecords),
      hierarchyEnabled: false,
      persisted: false,
    };
  }
  const merged = mergeOrphanVersionsIntoTree(tree, versionRecords);
  if (merged.changed) {
    await saveHierarchy(baseID, companyID, merged.tree);
  }
  return {
    tree: merged.tree,
    hierarchyEnabled: true,
    persisted: true,
  };
}
